import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormSegmentedButton } from '@ipfabric/ui-components/FormSegmentedButton/FormSegmentedButton';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { SegmentButton } from '@ipfabric/ui-components/SegmentedButton/SegmentButton/SegmentButton';
import { Tabs } from '@ipfabric/ui-components/Tabs/Tabs';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import type { SnapshotId } from '@ipfabric/utils';
import type { TestRegexSiteSeparationRuleResponseBody } from '@ipfabric/utils/schema/siteSeparation/siteSeparation';
import type { SiteSeparationAttributeTransformation } from '@ipfabric/utils/schema/siteSeparation/siteSeparationAttributeTransformation';
import { Table } from 'components/Table/components/Table';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useApi } from 'utils/request/useApi';
import { FormTagNameInput } from './FormTagNameInput';
import { RegexSiteNameField } from './RegexSiteNameField';
import { TRANSFORMATION_OPTIONS } from './RuleEditor.types';
import {
  type TestRuleFormValues,
  type TestRuleTab,
  type TestRuleValues,
  getTestRulePayload,
  getTestRuleTableColumns,
  getTestRuleTableData,
  getTestRuleTabs,
  toTestRuleFormValues,
  toTestRuleValues,
} from './TestRuleSidePanelContent.helpers';
import { useCloudResourceTagOptions } from './useCloudResourceTagOptions';

type TestRuleSidePanelContentProps = {
  initialValues: TestRuleValues;
  snapshotId: SnapshotId | null;
  onValuesChange: (values: TestRuleValues) => void;
};

export const TestRuleSidePanelContent = ({
  initialValues,
  snapshotId,
  onValuesChange,
}: TestRuleSidePanelContentProps) => {
  const api = useApi();
  const form = useCustomForm<TestRuleFormValues>({
    defaultValues: toTestRuleFormValues(initialValues),
  });
  const [activeTab, setActiveTab] = useState<TestRuleTab>('matched');
  const [result, setResult] =
    useState<TestRegexSiteSeparationRuleResponseBody | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const formValues = useWatch({
    control: form.control,
    defaultValue: toTestRuleFormValues(initialValues),
  }) as TestRuleFormValues;
  const selectedType = useWatch({
    control: form.control,
    name: 'type',
  });
  const {
    options: tagOptions,
    errorMessage: tagsError,
    loading: tagsLoading,
  } = useCloudResourceTagOptions(
    snapshotId,
    selectedType === 'cloudResourceTag',
  );
  // Free-text input; fetched names are suggestions only (see TagNameInput).
  const tagSuggestions = tagOptions.map(({ value }) => value);

  useEffect(() => {
    onValuesChange(toTestRuleValues(formValues));
  }, [formValues, onValuesChange]);

  const submitTestExpression = form.handleSubmit(async (formValues) => {
    const values = toTestRuleValues(formValues);
    setErrorMessage(null);
    setLoading(true);
    try {
      const response =
        await api.request<TestRegexSiteSeparationRuleResponseBody>({
          url:
            values.type === 'cloudResourceTag'
              ? '/settings/site-separation/cloud/test-tags'
              : '/settings/site-separation/test-regex',
          method: 'POST',
          data: getTestRulePayload(values, snapshotId),
        });
      setResult(response);
      setActiveTab('matched');
    } catch {
      setErrorMessage('Failed to test expression. Please try again.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  });

  const showSnmpLocation = selectedType === 'regexSnmpLocation';
  const isCloudResourceTag = selectedType === 'cloudResourceTag';
  // Both rule types can match cloud networks and vDevice rows in one run, so the
  // preview needs the Type column to say which a row came from.
  const showResourceType =
    selectedType === 'regexCloudResourceId' || isCloudResourceTag;
  const tableData = useMemo(
    () => getTestRuleTableData(result, activeTab),
    [activeTab, result],
  );
  const tableColumns = useMemo(
    () =>
      getTestRuleTableColumns(showSnmpLocation, showResourceType, activeTab),
    [activeTab, showResourceType, showSnmpLocation],
  );
  const frontendSimpleFilter = useFrontendLocalTable({
    columns: tableColumns,
    data: tableData,
  });
  const tabs = useMemo(() => getTestRuleTabs(result), [result]);

  return (
    <div className='flex h-full flex-col overflow-hidden drop-shadow'>
      <Card className='p-sm flex flex-col gap-sm'>
        <form className='flex flex-col gap-sm' onSubmit={submitTestExpression}>
          <FormSegmentedButton.Single<
            TestRuleFormValues,
            SiteSeparationAttributeTransformation
          >
            control={form.control}
            label='Text transformation (before regular expression match)'
            name='transformation'
          >
            {TRANSFORMATION_OPTIONS.map(({ label, value }) => (
              <SegmentButton key={value} value={value} className='flex-1'>
                {label}
              </SegmentButton>
            ))}
          </FormSegmentedButton.Single>
          {isCloudResourceTag ? (
            <FormTagNameInput
              control={form.control}
              dataTestId='site-separation-test-rule-tag-names'
              // Rendered in the field's own error slot rather than as a sibling
              // paragraph, so it is spaced and announced like any field error.
              externalError={tagsError ?? undefined}
              label='Selected tag(s)'
              loading={tagsLoading}
              name='tagNames'
              suggestions={tagSuggestions}
            />
          ) : (
            <>
              <FormTextField
                control={form.control}
                dataTestId='site-separation-test-rule-regex'
                label='Regular expression'
                name='regex'
                type='text'
              />
              <RegexSiteNameField
                control={form.control}
                dataTestId='site-separation-test-rule-site-name'
                label='Site name'
              />
            </>
          )}
          {(selectedType === 'regexCloudResourceId' || isCloudResourceTag) && (
            <FormSwitch
              control={form.control}
              label='Include cloud devices discovered via SSH or Vendor API'
              name='applyToCloudInstances'
            />
          )}
          <div>
            <Button
              className='w-full'
              dataTestId='site-separation-test-expression'
              loading={loading}
              type='submit'
              variant='outline'
            >
              Test expression
            </Button>
          </div>
        </form>

        {errorMessage && <p className='text-red-500'>{errorMessage}</p>}
      </Card>

      {result && (
        <div className='flex flex-1 flex-col min-h-0 mt-4'>
          <Tabs
            tabs={tabs}
            value={activeTab}
            onChange={setActiveTab}
            variant='outer'
          />
          <Table
            borderless
            className='rounded-b-lg flex flex-1 min-h-0 w-full pt-4'
            columns={tableColumns}
            disableContextMenu
            disableStickyColumn
            hideTitle
            rowIdField='id'
            rowHeight={40}
            noDataBox='No results found.'
            {...frontendSimpleFilter}
          />
        </div>
      )}
    </div>
  );
};
